// Canonical Neo response classification shared by manager-facing response surfaces.
// MR2: separates operational age from the action Neo expects from management.

const toDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfLocalDay = (date) => {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value;
};

const completed = (item = {}) => item.completed === true || String(item.status || "").toLowerCase() === "completed";

export function classifyOperationalAge(item = {}, now = new Date()) {
  if (completed(item)) return "live";

  const dueAt = toDate(item.dueAt || item.scheduledFor || item.dueDate);
  if (!dueAt) return "stale";

  const today = startOfLocalDay(now);
  const dueDay = startOfLocalDay(dueAt);
  const ageDays = Math.floor((today.getTime() - dueDay.getTime()) / 86400000);

  if (ageDays <= 0) return "live";
  if (ageDays === 1) return "carry_over";
  return "stale";
}

export function classifyResponseState(item = {}, now = new Date()) {
  if (completed(item)) return "resolved";

  const decision = item.responseDecision || item.managerDecision || {};
  const decisionStatus = String(decision.status || "").toLowerCase();
  const decisionRequired =
    decision.required === true ||
    item.managerDecisionRequired === true ||
    item.ownerResolutionStatus === "decision_required" ||
    ["required", "pending", "waiting_for_manager", "needs_decision"].includes(decisionStatus);

  if (decisionRequired) return "needs_decision";

  const managerStatus = String(item?.managerEscalation?.status || item.escalationStatus || "").toLowerCase();
  if (["waiting_for_manager", "failed"].includes(managerStatus)) return "needs_decision";

  const dueAt = toDate(item.dueAt);
  if (!dueAt || dueAt.getTime() > now.getTime()) return "watching";

  // Due/overdue/escalated duties that Neo is already chasing remain Watching
  // until the resolver explicitly requests a human decision.
  return "watching";
}

export function classifyResponseException(item = {}, now = new Date()) {
  return {
    operationalAge: classifyOperationalAge(item, now),
    responseState: classifyResponseState(item, now),
  };
}

export function isCurrentResponseException(item = {}, now = new Date()) {
  const { operationalAge, responseState } = classifyResponseException(item, now);
  return operationalAge !== "stale" && responseState !== "resolved";
}
